import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdvertisementBoard = () => {
  const navigate = useNavigate();
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackedImpressions, setTrackedImpressions] = useState(new Set());
  const [isTrackingImpression, setIsTrackingImpression] = useState(false);

  // Fetch advertisements from backend
  useEffect(() => {
    const fetchAdvertisements = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL;
        if (!API_BASE) {
          throw new Error('API URL not configured');
        }
        const response = await fetch(`${API_BASE}/advertisements/active`);
        const data = await response.json();

        if (data.success && data.data && data.data.length > 0) {
          setAdvertisements(data.data);
        } else {
          // Fallback to default advertisement if no ads found
          setAdvertisements([
            {
              id: 1,
              title: 'Advertise Here',
              description: 'Reach thousands of respiratory care professionals across Africa',
              image_url: null,
              link: '/contact',
              background_color: '#6B8E23',
              text_color: '#FFFFFF',
              company: 'ARCA Advertising'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching advertisements:', error);
        // Use fallback advertisement on error
        setAdvertisements([
          {
            id: 1,
            title: 'Advertise Here',
            description: 'Reach thousands of respiratory care professionals across Africa',
            image_url: null,
            link: '/contact',
            background_color: '#6B8E23',
            text_color: '#FFFFFF',
            company: 'ARCA Advertising'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvertisements();
  }, []);

  // Track advertisement impression (only once per ad per session)
  useEffect(() => {
    if (advertisements.length > 0 && advertisements[currentAdIndex] && !isTrackingImpression) {
      const currentAd = advertisements[currentAdIndex];
      const impressionKey = `${currentAd.id}-${Date.now()}`;

      // Only track if this ad hasn't been tracked in this session
      if (!trackedImpressions.has(currentAd.id)) {
        setIsTrackingImpression(true);

        const trackImpression = async () => {
          try {
            const API_BASE = process.env.REACT_APP_API_URL;
            if (!API_BASE) {
              throw new Error('API URL not configured');
            }
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

            await fetch(`${API_BASE}/advertisements/${currentAd.id}/impression`, {
              method: 'POST',
              signal: controller.signal
            });

            clearTimeout(timeoutId);

            // Mark this ad as tracked
            setTrackedImpressions(prev => new Set([...prev, currentAd.id]));
          } catch (error) {
            // Silently fail impression tracking - don't log to avoid console spam
            if (error.name !== 'AbortError') {
              // Only log non-timeout errors in development
              if (process.env.NODE_ENV === 'development') {
                console.debug('Impression tracking skipped:', error.message);
              }
            }
          } finally {
            setIsTrackingImpression(false);
          }
        };

        // Delay tracking by 1 second to ensure user actually sees the ad
        const timer = setTimeout(trackImpression, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [currentAdIndex, advertisements, trackedImpressions, isTrackingImpression]);

  // Rotate ads every 5 seconds
  useEffect(() => {
    if (advertisements.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prevIndex) =>
          (prevIndex + 1) % advertisements.length
        );
      }, 5000); // 5 seconds

      return () => clearInterval(interval);
    }
  }, [advertisements.length]);

  const currentAd = advertisements[currentAdIndex] || null;

  const handleAdClick = async () => {
    if (!currentAd) return;

    // Track click (with timeout to prevent hanging)
    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

      fetch(`${API_BASE}/advertisements/${currentAd.id}/click`, {
        method: 'POST',
        signal: controller.signal
      }).catch(() => {
        // Silently fail - don't block navigation
      }).finally(() => {
        clearTimeout(timeoutId);
      });
    } catch (error) {
      // Silently fail click tracking
    }

    // Navigate to link immediately (don't wait for tracking)
    if (currentAd.link) {
      if (currentAd.link.startsWith('http')) {
        window.open(currentAd.link, '_blank');
      } else {
        window.location.href = currentAd.link;
      }
    }
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (!currentAd || advertisements.length === 0) {
    return null; // Don't render if no advertisements
  }

  return (
    <div style={{
      backgroundColor: '#F5F5F5',
      padding: '0.75rem 0',
      borderTop: '1px solid #6B8E23',
      borderBottom: '1px solid #6B8E23'
    }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8 col-12">
            <div style={{
              position: 'relative',
              backgroundColor: currentAd.background_color,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'opacity 0.3s ease'
            }}
              onClick={handleAdClick}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.92'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              {/* Advertisement Image (if available) */}
              {currentAd.image_url && (
                <div style={{
                  width: '100%',
                  height: '160px',
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: currentAd.background_color
                }}>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${currentAd.image_url}`}
                    alt={currentAd.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      console.error('Failed to load advertisement image:', currentAd.image_url);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Advertisement Content */}
              <div style={{
                padding: '0.75rem 1rem',
                paddingBottom: '1.75rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <div style={{
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  color: currentAd.text_color,
                  opacity: 0.8,
                  marginBottom: '0.2rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {currentAd.company}
                </div>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  color: currentAd.text_color,
                  marginBottom: '0.35rem',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}>
                  {currentAd.title}
                </h3>
                <p style={{
                  fontSize: '0.8rem',
                  color: currentAd.text_color,
                  marginBottom: '0.5rem',
                  width: '100%',
                  maxWidth: '400px',
                  opacity: 0.95,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}>
                  {currentAd.description}
                </p>
                <div style={{
                  display: 'inline-block',
                  padding: '0.35rem 1rem',
                  backgroundColor: currentAd.text_color,
                  color: currentAd.background_color,
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap'
                }}>
                  Learn More →
                </div>
              </div>

              {/* Advertisement Label */}
              <div style={{
                position: 'absolute',
                top: '0.5rem',
                left: '0.5rem',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#FFFFFF',
                padding: '0.2rem 0.5rem',
                borderRadius: '3px',
                fontSize: '0.65rem',
                fontWeight: '600'
              }}>
                SPONSORED
              </div>

              {/* Navigation Dots */}
              <div style={{
                position: 'absolute',
                bottom: '0.5rem',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '0.4rem'
              }}>
                {advertisements.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentAdIndex(index);
                    }}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: index === currentAdIndex
                        ? currentAd.text_color
                        : 'rgba(255, 255, 255, 0.4)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    aria-label={`Go to ad ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Ad Info */}
            <div style={{
              marginTop: '0.5rem',
              textAlign: 'center'
            }}>
              <p style={{
                color: '#4A4F4F',
                fontSize: '0.75rem',
                margin: 0
              }}>
                <strong>Want to advertise here?</strong>{' '}
                <button
                  onClick={() => navigate('/contact')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6B8E23',
                    textDecoration: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit'
                  }}
                >
                  Contact us
                </button>{' '}
                to learn more.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvertisementBoard;
